import { useState, useRef } from "react";
import { UploadCloud, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { BRL, ScopeToggle } from "./shared";
import { Button } from "@/components/ui/button";

export default function ImportStatement({ scope, setScope }) {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    const ok = /\.(csv|pdf)$/i.test(f.name);
    if (!ok) return toast.error("Envie um arquivo CSV ou PDF.");
    setFile(f); setResult(null);
  };

  const upload = async () => {
    if (!file) return;
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("scope", scope);
      const { data } = await api.post("/statements/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.imported} transações importadas!`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="import-section">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-text-primary">Importar Extrato</h2>
          <p className="text-text-secondary font-body mt-1">Envie um extrato CSV ou PDF. A IA extrai e categoriza as transações.</p>
        </div>
        <ScopeToggle scope={scope} setScope={setScope} />
      </div>

      <div data-testid="dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-md p-12 text-center cursor-pointer transition-colors ${
          drag ? "border-primary bg-primary/5" : "border-stone-300 bg-white hover:border-primary/50"}`}>
        <input ref={inputRef} type="file" accept=".csv,.pdf" hidden data-testid="file-input"
          onChange={(e) => pick(e.target.files[0])} />
        {file ? (
          <div className="flex flex-col items-center gap-2 text-primary">
            <FileText className="h-10 w-10" />
            <p className="font-body font-medium">{file.name}</p>
            <p className="text-sm text-text-muted">Clique em processar para importar</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-text-muted">
            <UploadCloud className="h-10 w-10" />
            <p className="font-body">Arraste seu extrato aqui ou clique para selecionar</p>
            <p className="text-sm">Formatos aceitos: CSV, PDF</p>
          </div>
        )}
      </div>

      <Button data-testid="process-import" onClick={upload} disabled={!file || loading}
        className="rounded-md bg-primary hover:bg-primary/90 h-11">
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />}
        {loading ? "Processando com IA..." : "Processar extrato"}
      </Button>

      {result && (
        <div className="border border-stone-200 bg-white rounded-md p-6" data-testid="import-result">
          <div className="flex items-center gap-2 text-primary mb-4">
            <CheckCircle2 className="h-5 w-5" />
            <p className="font-heading font-bold">{result.imported} transações importadas</p>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {result.transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between text-sm font-body border-b border-stone-100 pb-2">
                <span className="text-text-secondary">{t.date} · {t.category} · {t.description}</span>
                <span className={t.type === "income" ? "text-primary font-medium" : "text-secondary font-medium"}>
                  {t.type === "income" ? "+" : "-"}{BRL(t.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
